import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { WorkspaceMembershipOut } from '@/features/workspaces/api/workspacesApi';

// Simple localStorage key read by ApiClient to inject X-Workspace-Id header.
// Must be a plain string (not Zustand's nested persist format) for easy reading.
export const WORKSPACE_LS_KEY = 'sm:wid';

interface WorkspaceState {
  activeWorkspaceId: string | null;
  memberships: WorkspaceMembershipOut[];

  setMemberships: (memberships: WorkspaceMembershipOut[]) => void;
  setActiveWorkspaceId: (id: string | null) => void;
  clearWorkspace: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  devtools(
    (set) => ({
      activeWorkspaceId: localStorage.getItem(WORKSPACE_LS_KEY),
      memberships: [],

      setMemberships(memberships) {
        set({ memberships });
      },

      setActiveWorkspaceId(id) {
        if (id) {
          localStorage.setItem(WORKSPACE_LS_KEY, id);
        } else {
          localStorage.removeItem(WORKSPACE_LS_KEY);
        }
        set({ activeWorkspaceId: id });
      },

      clearWorkspace() {
        localStorage.removeItem(WORKSPACE_LS_KEY);
        set({ activeWorkspaceId: null, memberships: [] });
      },
    }),
    { name: 'WorkspaceStore' }
  )
);

// Selectors
export const useActiveWorkspace = () =>
  useWorkspaceStore(
    useShallow((s) => {
      const active = s.memberships.find((m) => m.workspace.id === s.activeWorkspaceId);
      return {
        activeWorkspaceId: s.activeWorkspaceId,
        activeWorkspace: active ?? null,
        memberships: s.memberships,
        setActiveWorkspaceId: s.setActiveWorkspaceId,
      };
    })
  );
